import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Facebook, Linkedin, Twitter, Instagram, Youtube } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSocial } from '../contexts/SocialContext';
import { SocialShare } from './SocialShare';

export const Footer = () => {
  const { t } = useLanguage();
  const { social } = useSocial();
  const location = useLocation();
  const navigate = useNavigate();

  const socialLinks = [
    { key: 'facebook',  url: social.facebookUrl,  Icon: Facebook,  label: 'Facebook'  },
    { key: 'instagram', url: social.instagramUrl, Icon: Instagram, label: 'Instagram' },
    { key: 'linkedin',  url: social.linkedinUrl,  Icon: Linkedin,  label: 'LinkedIn'  },
    { key: 'twitter',   url: social.twitterUrl,   Icon: Twitter,   label: 'X / Twitter' },
    { key: 'youtube',   url: social.youtubeUrl,   Icon: Youtube,   label: 'YouTube'   },
  ].filter((l) => l.url);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black border-t border-[#C4A55A]/20 relative">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_4200a022-95b9-45c6-891b-2374fa355080/artifacts/ar8zvicq_color1_logo_transparent_background_ohne%20Rand.png"
                alt="Löwenherz Logo"
                className="h-12 w-auto"
              />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">{t.footer.about}</h3>
            <p className="text-gray-400 mb-4 max-w-md leading-relaxed">
              {t.footer.aboutText}
            </p>
            <div className="flex space-x-3">
              {socialLinks.map(({ key, url, Icon, label }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  data-testid={`footer-social-${key}`}
                  className="w-10 h-10 rounded-lg bg-[#C4A55A]/10 hover:bg-[#C4A55A]/20 flex items-center justify-center transition-colors duration-300 group"
                >
                  <Icon className="h-5 w-5 text-[#C4A55A] group-hover:text-[#D4B56A]" />
                </a>
              ))}
            </div>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Seite teilen</div>
              <SocialShare size="sm" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                >
                  {t.nav.home}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                >
                  {t.nav.about}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('projects')}
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                >
                  {t.nav.projects}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('investors')}
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                >
                  {t.nav.investors}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                >
                  {t.nav.contact}
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{t.footer.legal}</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/datenschutz"
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                  data-testid="footer-link-datenschutz"
                >
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link
                  to="/impressum"
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                  data-testid="footer-link-impressum"
                >
                  {t.footer.imprint}
                </Link>
              </li>
              <li>
                <Link
                  to="/agb"
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                  data-testid="footer-link-agb"
                >
                  AGB
                </Link>
              </li>
              <li>
                <Link
                  to="/risikohinweise"
                  className="text-gray-400 hover:text-[#C4A55A] transition-colors duration-300"
                  data-testid="footer-link-risiko"
                >
                  Risikohinweise
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#C4A55A]/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">{t.footer.rights}</p>
            <div className="flex items-center flex-wrap justify-center gap-4">
              <div className="flex items-center text-gray-400 text-sm">
                <Building2 className="h-4 w-4 mr-2 text-[#C4A55A]" />
                <span>Löwenherz Invest AG</span>
              </div>
              <a
                href="https://app.emergent.sh/?utm_source=emergent-badge"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="emergent-badge-footer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white text-xs transition-colors"
                title="Made with Emergent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="none" className="opacity-70">
                  <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="currentColor"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="currentColor"/>
                </svg>
                Made with Emergent
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};